/**
 * Smart Autocomplete
 * Intelligent autocomplete with error correction and learning capabilities
 */

"use strict";

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);


/**
 * Smart Autocomplete Engine
 */
export const SmartAutocomplete = {
    // Configuration
    config: {
        minQueryLength: 2,
        maxSuggestions: 8,
        errorCorrection: true,
        learningEnabled: true,
        popularityWeight: 0.4,
        proximityWeight: 0.3,
        frequencyWeight: 0.3,
        debounceDelay: 100
    },

    // Internal state
    suggestions: [],
    queryHistory: new Map(),
    corrections: new Map(),
    popularTerms: new Map(),
    
    // Cache for performance
    cache: new Map(),
    
    // Statistics
    stats: {
        queries: 0,
        corrections: 0,
        acceptedSuggestions: 0,
        cacheHits: 0
    },

    /**
     * Initialize smart autocomplete
     */
    initialize() {
        this.loadUserData();
        this.setupEventListeners();
        return this;
    },

    /**
     * Get autocomplete suggestions for query
     * @param {string} query - User input query
     * @param {Array} dataset - Data to search in
     * @param {Object} options - Options for suggestions
     * @returns {Array} Array of suggestions
     */
    getSuggestions(query, dataset, options = {}) {
        const trimmedQuery = query.trim();
        
        if (trimmedQuery.length < this.config.minQueryLength) {
            return [];
        }

        this.stats.queries++;
        
        // Check cache first
        const cacheKey = `${trimmedQuery}|${JSON.stringify(options)}`;
        if (this.cache.has(cacheKey)) {
            this.stats.cacheHits++;
            return this.cache.get(cacheKey);
        }

        // Generate suggestions
        const suggestions = this.generateSuggestions(trimmedQuery, dataset, options);
        
        // Cache results
        this.cache.set(cacheKey, suggestions);
        
        // Update query history
        this.updateQueryHistory(trimmedQuery);
        
        return suggestions;
    },

    /**
     * Generate intelligent suggestions
     * @param {string} query - Search query
     * @param {Array} dataset - Dataset to search
     * @param {Object} options - Generation options
     * @returns {Array} Generated suggestions
     */
    generateSuggestions(query, dataset, options) {
        const suggestions = new Set();
        const normalizedQuery = query.toLowerCase();
        
        // 1. Exact prefix matches from popular terms
        this.addPopularMatches(normalizedQuery, suggestions);
        
        // 2. Fuzzy matches with error correction
        if (this.config.errorCorrection) {
            this.addCorrectedMatches(normalizedQuery, suggestions);
        }
        
        // 3. Contextual matches from dataset
        this.addContextualMatches(normalizedQuery, dataset, suggestions, options);
        
        // 4. Historical matches from user queries
        this.addHistoricalMatches(normalizedQuery, suggestions);
        
        // Convert to array and score
        const scoredSuggestions = Array.from(suggestions).map(suggestion => ({
            ...suggestion,
            score: this.calculateSuggestionScore(suggestion, normalizedQuery)
        }));
        
        // Sort by score and limit
        return scoredSuggestions
            .sort((a, b) => b.score - a.score)
            .slice(0, this.config.maxSuggestions);
    },

    /**
     * Add popular term matches
     * @param {string} query - Normalized query
     * @param {Set} suggestions - Suggestions set to add to
     */
    addPopularMatches(query, suggestions) {
        for (const [term, frequency] of this.popularTerms) {
            if (term.startsWith(query) && term !== query) {
                suggestions.add({
                    text: term,
                    type: 'popular',
                    frequency,
                    source: 'popular_terms'
                });
            }
        }
    },

    /**
     * Add error-corrected matches
     * @param {string} query - Normalized query
     * @param {Set} suggestions - Suggestions set to add to
     */
    addCorrectedMatches(query, suggestions) {
        // Check for common typos and corrections
        const correction = this.findBestCorrection(query);
        if (correction && correction !== query) {
            suggestions.add({
                text: correction,
                type: 'correction',
                original: query,
                confidence: this.calculateCorrectionConfidence(query, correction),
                source: 'error_correction'
            });
        }
        
        // Add phonetic matches
        const phoneticMatches = this.findPhoneticMatches(query);
        phoneticMatches.forEach(match => {
            suggestions.add({
                text: match,
                type: 'phonetic',
                original: query,
                source: 'phonetic_matching'
            });
        });
    },

    /**
     * Add contextual matches from dataset
     * @param {string} query - Normalized query
     * @param {Array} dataset - Dataset to search
     * @param {Set} suggestions - Suggestions set to add to
     * @param {Object} options - Search options
     */
    addContextualMatches(query, dataset, suggestions, options) {
        const searchFields = options.searchFields || ['name', 'title', 'n'];
        const sampleSize = Math.min(dataset.length, 500); // Performance limit
        
        for (let i = 0; i < sampleSize; i++) {
            const item = dataset[i];
            
            for (const field of searchFields) {
                if (!item[field]) continue;
                
                const text = item[field].toLowerCase();
                // Unicode-aware tokenizer: covers EN/RU/DE/FR/ES diacritics (Tätigkeit, août, año).
                const words = text.match(/[\p{L}\p{N}]+/gu) || [];
                
                for (const word of words) {
                    if (word.length > 2 && word.startsWith(query) && word !== query) {
                        suggestions.add({
                            text: word,
                            type: 'contextual',
                            field,
                            source: 'dataset_context'
                        });
                    }
                }
            }
        }
    },

    /**
     * Add historical matches from user queries
     * @param {string} query - Normalized query
     * @param {Set} suggestions - Suggestions set to add to
     */
    addHistoricalMatches(query, suggestions) {
        for (const [historicalQuery, data] of this.queryHistory) {
            if (historicalQuery.startsWith(query) && historicalQuery !== query) {
                suggestions.add({
                    text: historicalQuery,
                    type: 'historical',
                    frequency: data.count,
                    lastUsed: data.lastUsed,
                    source: 'user_history'
                });
            }
        }
    },

    /**
     * Calculate suggestion score
     * @param {Object} suggestion - Suggestion object
     * @param {string} query - Original query
     * @returns {number} Suggestion score
     */
    calculateSuggestionScore(suggestion, query) {
        let score = 0;
        
        // Base score by type
        const typeScores = {
            popular: 0.8,
            correction: 0.7,
            contextual: 0.6,
            historical: 0.9,
            phonetic: 0.5
        };
        score += typeScores[suggestion.type] || 0.3;
        
        // Frequency/popularity bonus
        if (suggestion.frequency) {
            score += Math.min(suggestion.frequency / 100, 0.3) * this.config.popularityWeight;
        }
        
        // Proximity bonus (how close to query start)
        if (suggestion.text.startsWith(query)) {
            const proximityRatio = query.length / suggestion.text.length;
            score += proximityRatio * this.config.proximityWeight;
        }
        
        // Recency bonus for historical queries
        if (suggestion.lastUsed) {
            const hoursSince = (Date.now() - suggestion.lastUsed) / (1000 * 60 * 60);
            const recencyBonus = Math.max(0, 1 - hoursSince / 168) * 0.2; // Week decay
            score += recencyBonus;
        }
        
        // Confidence bonus for corrections
        if (suggestion.confidence) {
            score += suggestion.confidence * 0.3;
        }
        
        return Math.min(score, 1); // Cap at 1.0
    },

    /**
     * Find best spelling correction for query
     * @param {string} query - Query to correct
     * @returns {string|null} Best correction or null
     */
    findBestCorrection(query) {
        if (query.length < 3) return null;
        
        let bestCorrection = null;
        let bestDistance = Infinity;
        
        // Check against popular terms
        for (const [term] of this.popularTerms) {
            if (Math.abs(term.length - query.length) <= 2) {
                const distance = this.levenshteinDistance(query, term);
                if (distance < bestDistance && distance <= 2) {
                    bestDistance = distance;
                    bestCorrection = term;
                }
            }
        }
        
        // Check against common corrections
        if (this.corrections.has(query)) {
            return this.corrections.get(query);
        }
        
        return bestCorrection;
    },

    /**
     * Find phonetic matches using simplified Soundex
     * @param {string} query - Query to find matches for
     * @returns {Array} Array of phonetic matches
     */
    findPhoneticMatches(query) {
        const querySoundex = this.soundex(query);
        const matches = [];
        
        for (const [term] of this.popularTerms) {
            if (term !== query && this.soundex(term) === querySoundex) {
                matches.push(term);
            }
        }
        
        return matches.slice(0, 3); // Limit phonetic matches
    },

    /**
     * Calculate Levenshtein distance
     * @param {string} str1 - First string
     * @param {string} str2 - Second string
     * @returns {number} Edit distance
     */
    levenshteinDistance(str1, str2) {
        const matrix = [];
        
        for (let i = 0; i <= str2.length; i++) {
            matrix[i] = [i];
        }
        
        for (let j = 0; j <= str1.length; j++) {
            matrix[0][j] = j;
        }
        
        for (let i = 1; i <= str2.length; i++) {
            for (let j = 1; j <= str1.length; j++) {
                if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j - 1] + 1,
                        matrix[i][j - 1] + 1,
                        matrix[i - 1][j] + 1
                    );
                }
            }
        }
        
        return matrix[str2.length][str1.length];
    },

    /**
     * Calculate correction confidence
     * @param {string} original - Original query
     * @param {string} correction - Suggested correction
     * @returns {number} Confidence score (0-1)
     */
    calculateCorrectionConfidence(original, correction) {
        const distance = this.levenshteinDistance(original, correction);
        const maxLength = Math.max(original.length, correction.length);
        return 1 - (distance / maxLength);
    },

    /**
     * Simplified Soundex algorithm for phonetic matching
     * @param {string} str - String to encode
     * @returns {string} Soundex code
     */
    soundex(str) {
        const firstLetter = str.charAt(0).toUpperCase();
        const consonants = str.toLowerCase()
            .replace(/[hwyu]/g, '')
            .replace(/[bfpv]/g, '1')
            .replace(/[cgjkqsxz]/g, '2')
            .replace(/[dt]/g, '3')
            .replace(/[l]/g, '4')
            .replace(/[mn]/g, '5')
            .replace(/[r]/g, '6')
            .replace(/[^0-9]/g, '');
        
        return (firstLetter + consonants + '000').substring(0, 4);
    },

    /**
     * Update query history for learning
     * @param {string} query - Query to record
     */
    updateQueryHistory(query) {
        if (!this.config.learningEnabled) return;
        
        const existing = this.queryHistory.get(query) || { count: 0, lastUsed: 0 };
        this.queryHistory.set(query, {
            count: existing.count + 1,
            lastUsed: Date.now()
        });
        
        // Limit history size
        if (this.queryHistory.size > 1000) {
            // Remove oldest entries
            const entries = Array.from(this.queryHistory.entries())
                .sort((a, b) => b[1].lastUsed - a[1].lastUsed)
                .slice(0, 800);
            this.queryHistory = new Map(entries);
        }
    },

    /**
     * Learn from user selection
     * @param {string} query - Original query
     * @param {string} selected - Selected suggestion
     */
    learnFromSelection(query, selected) {
        this.stats.acceptedSuggestions++;
        
        // If user corrected a typo, remember it
        if (query !== selected && this.levenshteinDistance(query, selected) <= 2) {
            this.corrections.set(query, selected);
            this.stats.corrections++;
        }
        
        // Increase popularity of selected term
        const currentCount = this.popularTerms.get(selected) || 0;
        this.popularTerms.set(selected, currentCount + 1);
        
        if (debugMode === '1') {
                console.log(`[SmartAutocomplete] Learned: "${query}" → "${selected}"`);
            }
    },

    /**
     * Setup event listeners for learning
     */
    setupEventListeners() {
        // This would be called when integrating with UI
    },

    /**
     * Load user data from localStorage
     */
    loadUserData() {
        try {
            const data = localStorage.getItem('smartAutocompleteData');
            if (data) {
                const parsed = JSON.parse(data);
                this.queryHistory = new Map(parsed.queryHistory || []);
                this.corrections = new Map(parsed.corrections || []);
                this.popularTerms = new Map(parsed.popularTerms || []);
                this.stats = { ...this.stats, ...parsed.stats };
            }
        } catch (error) {
            if (debugMode === '1') {
                console.warn('[SmartAutocomplete] Failed to load user data:', error);
            }
        }
    },

    /**
     * Save user data to localStorage
     */
    saveUserData() {
        try {
            const data = {
                queryHistory: Array.from(this.queryHistory.entries()),
                corrections: Array.from(this.corrections.entries()),
                popularTerms: Array.from(this.popularTerms.entries()),
                stats: this.stats,
                lastSaved: Date.now()
            };
            localStorage.setItem('smartAutocompleteData', JSON.stringify(data));
        } catch (error) {
            if (debugMode === '1') {
                console.warn('[SmartAutocomplete] Failed to save user data:', error);
            }
        }
    },

    /**
     * Get autocomplete statistics
     * @returns {Object} Statistics object
     */
    getStats() {
        return {
            ...this.stats,
            cacheSize: this.cache.size,
            historySize: this.queryHistory.size,
            correctionsCount: this.corrections.size,
            popularTermsCount: this.popularTerms.size,
            cacheHitRate: this.stats.queries > 0 ? (this.stats.cacheHits / this.stats.queries) : 0,
            correctionRate: this.stats.queries > 0 ? (this.stats.corrections / this.stats.queries) : 0
        };
    },

    /**
     * Clear all data and reset
     */
    reset() {
        this.queryHistory.clear();
        this.corrections.clear();
        this.popularTerms.clear();
        this.cache.clear();
        this.stats = {
            queries: 0,
            corrections: 0,
            acceptedSuggestions: 0,
            cacheHits: 0
        };
        localStorage.removeItem('smartAutocompleteData');
        if (debugMode === '1') {
                console.log('[SmartAutocomplete] Reset completed');
            }
    }
};

// Auto-initialize
SmartAutocomplete.initialize(); 